import type * as React from 'react';

import type { NotificationMetadata } from '@gitbook/app-notifications';
import { BadgeIcon, type BadgeKind, sizes, useBackgroundColor } from '@gitbook/react-uikit';
import { Icon, type SVGIconComponent } from '@gitbook/spine-icons';
import { AvatarSizeFromPixelValue } from '@gitbook/spine-react';

import { UserAvatar } from '../Users';

/**
 * Render an appropriate notification for an icon. If the notification has
 * an actor associated with it, render a stacked icon. Otherwise, render a
 * simple icon.
 */
export function NotificationIcon(props: {
    metadata: NotificationMetadata | null;
    size?: number;
}): React.ReactElement {
    const { metadata } = props;
    const actor = getActorUserId(metadata);

    return actor ? <StackedNotificationIcon {...props} actor={actor} /> : <ActionIcon {...props} />;
}

/**
 * A notification icon with both event and actor icons.
 */
function StackedNotificationIcon(props: {
    metadata: NotificationMetadata | null;
    actor: string;
    size?: number;
    className?: string;
}): React.ReactElement {
    const { metadata, actor, size = sizes.ICON_600, className } = props;
    const backgroundColor = useBackgroundColor();

    const smallerIconSize = size * 0.55;

    return (
        <div className={`relative ${className}`}>
            <UserAvatar size={AvatarSizeFromPixelValue[size]} user={actor} />
            <div
                style={{
                    backgroundColor,
                    borderRadius: smallerIconSize,
                    right: -smallerIconSize / 2,
                    bottom: -smallerIconSize / 3,
                }}
                className={'absolute'}
            >
                <ActionIcon size={smallerIconSize} metadata={metadata} />
            </div>
        </div>
    );
}

/**
 * An icon showing the action of the notification.
 */
function ActionIcon(props: {
    metadata: NotificationMetadata | null;
    size?: number;
}): React.ReactElement {
    const { metadata, size = sizes.ICON_600, ...iconProps } = props;
    const badgeProps: { icon: SVGIconComponent; kind: BadgeKind } = (() => {
        switch (metadata?.type) {
            case 'change_request_opened':
                return { icon: Icon.ChangeRequestOpen, kind: 'info' };
            case 'change_request_review_requested':
                return { icon: Icon.ChangeRequestReviewRequest, kind: 'info' };
            case 'comment_mention':
                return { icon: Icon.AtSign, kind: 'info' };
            case 'comment_posted':
            case 'comment_reply':
                return { icon: Icon.Comment, kind: 'info' };
            case 'upgrade_requested':
                return { icon: Icon.Upgrade, kind: 'upgrade' };
        }

        return { icon: Icon.ExclamationCircle, kind: 'info' };
    })();

    return <BadgeIcon {...iconProps} {...badgeProps} size={size} />;
}

/**
 * An icon for the actor of a notification. Fallsback to a generic user icon if needed.
 */
function getActorUserId(metadata: NotificationMetadata | null): string | undefined {
    switch (metadata?.type) {
        case 'change_request_opened':
            return metadata.actor;

        case 'comment_mention':
        case 'comment_posted':
        case 'comment_reply':
            return metadata.postedBy;

        case 'change_request_review_requested':
            return metadata.requestedBy;
    }
}
